import React, { useCallback, useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { DialerRow, ProspectCatalogs } from "../../../services/v2/prospects.service";
import { errorTexto, nombreCompleto, statusClase } from "./salesHelpers";
import ClienteView from "./clienteView";

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	etapa: number;
	owner: string;
	onSalir: () => void;
}

/**
 * Marcador (Esteban, 2026-09-13): la asesora llama a SU cartera, en orden de probabilidad — seguimientos vencidos,
 * quien escribió hace poco, los nuevos, las promesas — y no pierde tiempo buscando a quién llamar. Cada llamada
 * queda registrada sola; al guardar cómo terminó pasa a la siguiente persona.
 */
const MarcadorView: React.FC<Props> = ({ campusId, catalogs, etapa, owner, onSalir }) => {
	const http = HttpManager.getInstance();
	const [cola, setCola] = useState<DialerRow[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [actual, setActual] = useState<number | null>(null);
	const [hechas, setHechas] = useState<{ [id: number]: string }>({});
	const [auto, setAuto] = useState<boolean>(false);
	const [cargando, setCargando] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const cargar = useCallback(async () => {
		setCargando(true); setError(null);
		try {
			const r = await http.prospectsService.dialer(campusId, { etapa, owner }, 80);
			setCola(r.data.queue); setTotal(r.data.total);
		} catch (e) { setError(errorTexto(e)); }
		finally { setCargando(false); }
	}, [campusId, etapa, owner]);

	useEffect(() => { cargar(); setActual(null); setHechas({}); }, [cargar]);

	const pendientes = cola.filter((r) => !hechas[r.id]);
	const siguiente = (despuesDe: number | null) => {
		const resto = cola.filter((r) => !hechas[r.id] && r.id !== despuesDe);
		setActual(resto.length ? resto[0].id : null);
		if (!resto.length) cargar();
	};
	const empezar = () => { setAuto(true); if (pendientes.length) setActual(pendientes[0].id); };
	const saltar = () => { if (actual) { setHechas({ ...hechas, [actual]: "saltado" }); siguiente(actual); } };

	return (
		<div className="ventas-cuerpo marcador">
			<div className="mc-cola">
				<div className="mc-cab">
					<div>
						<b>Marcador</b>
						<span className="apagado"> · {total.toLocaleString("es-MX")} por llamar</span>
					</div>
					<button className="enlace" onClick={onSalir}>Volver a la bandeja</button>
				</div>
				<div className="mc-acciones">
					{!auto ? <button className="btn-wa pri" disabled={!pendientes.length} onClick={empezar}>Empezar a llamar</button>
						: <button className="btn-wa" onClick={() => setAuto(false)}>Pausar</button>}
					{auto && actual ? <button className="btn-wa" onClick={saltar}>Saltar</button> : null}
					<button className="btn-wa" onClick={cargar} disabled={cargando}>Actualizar</button>
				</div>
				{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
				<div className="filas">
					{cola.map((r, i) => (
						<button key={r.id} className={"ventas-fila" + (actual === r.id ? " activa" : "") + (hechas[r.id] ? " hecha" : "")} onClick={() => setActual(r.id)}>
							<div className="l1">
								<span className="nombre"><span className="mc-num">{i + 1}</span>{nombreCompleto(r)}</span>
								{hechas[r.id] ? <span className="cuando">{hechas[r.id] === "saltado" ? "saltado" : "✓"}</span> : null}
							</div>
							<div className="l2">{[telefonoBonito(r.phone_e164), r.course_interest_text].filter(Boolean).join(" · ")}</div>
							<div className="chips">
								<span className={"chip " + statusClase(r.status_id)}>{r.status}</span>
								{r.reasons.filter((x) => x !== r.status).slice(0, 3).map((x) => <span key={x} className="chip razon">{x}</span>)}
							</div>
						</button>
					))}
					{!cargando && !cola.length ? <div className="ventas-vacio">Nadie por llamar ahora. Los que no contestaron vuelven a salir en 2 horas.</div> : null}
				</div>
			</div>
			{actual ? (
				<ClienteView key={actual} campusId={campusId} prospectId={actual} catalogs={catalogs} onChanged={() => { /* la cola se refresca al terminar */ }}
					onOpen={setActual} onBack={() => setActual(null)}
					marcador={{ autoLlamar: auto, onResultado: (res) => { setHechas((h) => ({ ...h, [actual]: res })); if (auto) siguiente(actual); } }} />
			) : (
				<div className="ventas-cliente">
					<div className="ventas-vacio mc-inicio">
						<h3>Tu lista de llamadas de hoy</h3>
						<p>Primero los seguimientos que vencen, luego quien escribió hace poco, los nuevos y las promesas. Quien no contestó 6 veces seguidas sale de la lista.</p>
						<button className="btn-wa pri" disabled={!pendientes.length} onClick={empezar}>Empezar a llamar</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default MarcadorView;
